/**
 * Frame quality measurement (pipeline stage 2, measurement half).
 *
 * The previous implementation reported no quality information at all, which meant the
 * §20 uncertainty machinery had nothing to work with: a pitch-dark frame produced a
 * confident-looking head pose and the employee wore the consequences of a badly positioned
 * camera. These two numbers are what let the pipeline say "insufficient data" instead.
 *
 * Both are measured on a heavily downscaled copy of the frame. Sampling a 64-wide thumbnail
 * costs microseconds and is entirely adequate: we need "is this frame dark" and "is this
 * frame sharp", not a photometric analysis.
 */

import type { FrameMetrics } from '../core/index.js';

const SAMPLE_WIDTH = 64;

export class FrameMetricsSampler {
  // The 2D context keeps its own canvas alive, so no separate canvas field is needed.
  private context: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D | null = null;
  private width = 0;
  private height = 0;

  /**
   * Measure brightness and sharpness for the current video frame.
   *
   * Returns null when the frame cannot be sampled — a canvas that is unavailable or tainted
   * must degrade to "we did not measure quality", never to a fabricated good reading.
   */
  sample(video: HTMLVideoElement): FrameMetrics | null {
    const sourceWidth = video.videoWidth;
    const sourceHeight = video.videoHeight;
    if (sourceWidth === 0 || sourceHeight === 0) return null;

    const targetWidth = Math.min(SAMPLE_WIDTH, sourceWidth);
    const targetHeight = Math.max(1, Math.round((sourceHeight / sourceWidth) * targetWidth));

    const context = this.ensureContext(targetWidth, targetHeight);
    if (context === null) return null;

    try {
      context.drawImage(video, 0, 0, targetWidth, targetHeight);
      const { data } = context.getImageData(0, 0, targetWidth, targetHeight);
      return computeMetrics(data, targetWidth, targetHeight);
    } catch {
      // Security errors on a tainted canvas, or a frame that vanished mid-draw.
      return null;
    }
  }

  private ensureContext(
    width: number,
    height: number,
  ): CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D | null {
    if (this.context !== null && this.width === width && this.height === height) {
      return this.context;
    }

    this.width = width;
    this.height = height;

    if (typeof OffscreenCanvas !== 'undefined') {
      const canvas = new OffscreenCanvas(width, height);
      this.context = canvas.getContext('2d', { willReadFrequently: true });
      return this.context;
    }

    if (typeof document === 'undefined') return null;
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    this.context = canvas.getContext('2d', { willReadFrequently: true });
    return this.context;
  }

  dispose(): void {
    this.context = null;
  }
}

/**
 * Mean luma plus the variance of a 4-neighbour Laplacian.
 *
 * Variance-of-Laplacian is the standard cheap focus measure: a blurred image has little
 * high-frequency energy, so the second derivative is small everywhere and its variance
 * collapses. The absolute value depends on resolution and sensor, which is exactly why the
 * threshold it is compared against is policy rather than a constant.
 */
export function computeMetrics(
  rgba: Uint8ClampedArray | Uint8Array,
  width: number,
  height: number,
): FrameMetrics {
  const pixelCount = width * height;
  const luma = new Float32Array(pixelCount);

  let brightnessTotal = 0;
  for (let index = 0; index < pixelCount; index += 1) {
    const offset = index * 4;
    // Rec. 601 luma.
    const value =
      0.299 * (rgba[offset] ?? 0) +
      0.587 * (rgba[offset + 1] ?? 0) +
      0.114 * (rgba[offset + 2] ?? 0);
    luma[index] = value;
    brightnessTotal += value;
  }

  const brightness = brightnessTotal / pixelCount;

  let laplacianTotal = 0;
  let laplacianSquaredTotal = 0;
  let samples = 0;
  for (let y = 1; y < height - 1; y += 1) {
    for (let x = 1; x < width - 1; x += 1) {
      const index = y * width + x;
      const response =
        4 * (luma[index] as number) -
        (luma[index - 1] as number) -
        (luma[index + 1] as number) -
        (luma[index - width] as number) -
        (luma[index + width] as number);
      laplacianTotal += response;
      laplacianSquaredTotal += response * response;
      samples += 1;
    }
  }

  if (samples === 0) {
    // A frame too small to have interior pixels tells us nothing about sharpness. Report
    // zero variance and let the policy floor decide, rather than inventing a value.
    return { brightness, blurVariance: 0 };
  }

  const mean = laplacianTotal / samples;
  const blurVariance = Math.max(0, laplacianSquaredTotal / samples - mean * mean);
  return { brightness, blurVariance };
}
